import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Paper,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';

const PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash' },
  { value: 'PAYNOW', label: 'UPI' },
  { value: 'CREDIT_CARD', label: 'Card' },
  { value: 'CREDIT', label: 'Credit' },
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

/**
 * Split Bill Dialog - allows user to distribute payment across multiple methods
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Close handler
 * @param {number} props.totalAmount - Total bill amount to pay
 * @param {Function} props.onConfirm - Called with split data: { payments: [{ mode, amount, refNo }], total }
 * @param {boolean} props.requireCustomerForCredit - If true, show warning when Credit is used (caller should validate customer)
 */
const SplitBillDialog = ({
  open,
  onClose,
  totalAmount,
  onConfirm,
  requireCustomerForCredit = false,
  hasCustomer = false,
}) => {
  const [payments, setPayments] = useState([
    { mode: 'CASH', amount: '', refNo: '' },
  ]);
  const [error, setError] = useState('');
  const modeRefs = useRef([]);
  const amountRefs = useRef([]);
  const refNoRefs = useRef([]);

  const totalEntered = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const balanceToPay = Math.max(0, (totalAmount || 0) - totalEntered);
  const isValid = Math.abs(totalEntered - (totalAmount || 0)) < 0.01; // Allow small float tolerance

  useEffect(() => {
    if (open) {
      setPayments([{ mode: 'CASH', amount: '', refNo: '' }]);
      setError('');
    }
  }, [open, totalAmount]);

  // Focus first Payment Mode when dialog opens
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => {
        const el = modeRefs.current[0];
        if (el) el.focus?.();
      }, 100);
      return () => clearTimeout(t);
    }
  }, [open]);

  const handleAddRow = useCallback(() => {
    setPayments((prev) => {
      const next = [...prev, { mode: 'CASH', amount: '', refNo: '' }];
      setTimeout(() => {
        const idx = next.length - 1;
        const el = modeRefs.current[idx];
        if (el) el.focus?.();
      }, 50);
      return next;
    });
  }, []);

  const focusNextField = useCallback((rowIndex, field) => {
    if (field === 'mode') {
      setTimeout(() => amountRefs.current[rowIndex]?.focus?.(), 50);
    } else if (field === 'amount') {
      refNoRefs.current[rowIndex]?.focus?.();
    } else if (field === 'refNo') {
      handleAddRow();
    }
  }, [handleAddRow]);

  const handleRemoveRow = (index) => {
    if (payments.length <= 1) return;
    setPayments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    setPayments((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
    );
    setError('');
  };

  const handleConfirm = () => {
    const validPayments = payments
      .map((p) => ({
        mode: p.mode,
        amount: parseFloat(p.amount) || 0,
        refNo: String(p.refNo || '').trim(),
      }))
      .filter((p) => p.amount > 0);

    if (validPayments.length === 0) {
      setError('Enter at least one payment with amount > 0');
      return;
    }

    const sum = validPayments.reduce((s, p) => s + p.amount, 0);
    if (Math.abs(sum - (totalAmount || 0)) > 0.01) {
      setError(`Total entered (₹${sum.toFixed(2)}) must equal bill amount (₹${(totalAmount || 0).toFixed(2)})`);
      return;
    }

    const hasCredit = validPayments.some((p) => p.mode === 'CREDIT');
    if (hasCredit && requireCustomerForCredit && !hasCustomer) {
      setError('Customer selection is required when using Credit in split bill');
      return;
    }

    const splitData = {
      payments: validPayments,
      total: totalAmount,
    };
    onConfirm(splitData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: '#1976d2',
          color: 'white',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        Split Bill Payment (F2)
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
          ×
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" color="primary" fontWeight="bold">
            TOTAL BILL AMOUNT: {formatCurrency(totalAmount)}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: balanceToPay === 0 ? 'success.main' : 'warning.main',
              fontWeight: 'bold',
              mt: 1,
            }}
          >
            BALANCE TO PAY: {formatCurrency(balanceToPay)}
          </Typography>
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell><strong>Payment Mode</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Ref No / Remarks</strong></TableCell>
                <TableCell align="right" width={60}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Mode</InputLabel>
                      <Select
                        value={row.mode}
                        label="Mode"
                        onChange={(e) => handleChange(index, 'mode', e.target.value)}
                        onClose={() => setTimeout(() => amountRefs.current[index]?.focus?.(), 50)}
                        inputRef={(el) => { if (el) modeRefs.current[index] = el; }}
                      >
                        {PAYMENT_MODES.map((m) => (
                          <MenuItem key={m.value} value={m.value}>
                            {m.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={row.amount}
                      onChange={(e) => handleChange(index, 'amount', e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          focusNextField(index, 'amount');
                        }
                      }}
                      inputRef={(el) => { if (el) amountRefs.current[index] = el; }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        inputProps: { min: 0, step: 0.01 },
                      }}
                      sx={{ width: 120 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Ref No / Remarks"
                      value={row.refNo}
                      onChange={(e) => handleChange(index, 'refNo', e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          focusNextField(index, 'refNo');
                        }
                      }}
                      inputRef={(el) => { if (el) refNoRefs.current[index] = el; }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveRow(index)}
                      disabled={payments.length <= 1}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
          >
            Add Payment Mode
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Press Enter to move between fields; Enter on Ref No adds new row
          </Typography>
        </Box>

        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="body1" fontWeight="bold">
            Total Entered: {formatCurrency(totalEntered)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirm}
          disabled={!isValid}
        >
          SAVE & PRINT
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitBillDialog;
